// Automation Service
// Frontend service for automation API calls

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

class AutomationService {
  
  /**
   * Get pending automation decisions
   */
  async getPendingDecisions(companyId, brandId = null) {
    try {
      const params = { companyId };
      if (brandId) {
        params.brandId = brandId;
      }

      const response = await axios.get(`${API_BASE_URL}/api/automation/decisions/pending`, {
        params,
        withCredentials: true
      });

      return response.data;
    } catch (error) {
      console.error('Error getting pending decisions:', error);
      throw error;
    }
  }

  /**
   * Get all automation decisions with filters
   */
  async getAllDecisions(params = {}) {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/automation/decisions`, {
        params: {
          page: 1,
          limit: 20,
          ...params
        },
        withCredentials: true
      });

      return response.data;
    } catch (error) {
      console.error('Error getting automation decisions:', error);
      throw error;
    }
  }

  /**
   * Create a new automation decision
   */
  async createDecision(decisionData) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/automation/decisions`,
        decisionData,
        { withCredentials: true }
      );

      return response.data;
    } catch (error) {
      console.error('Error creating automation decision:', error);
      throw error;
    }
  }

  /**
   * Update decision status (approve/reject)
   */
  async updateDecisionStatus(decisionId, status, reason = null) {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/automation/decisions/${decisionId}/status`,
        { status, reason },
        { withCredentials: true }
      );

      return response.data;
    } catch (error) {
      console.error('Error updating decision status:', error);
      throw error;
    }
  }

  /**
   * Get automation statistics for dashboard
   */
  async getAutomationStats(companyId, brandId = null) {
    try {
      const params = { companyId };
      if (brandId) {
        params.brandId = brandId;
      }

      const response = await axios.get(`${API_BASE_URL}/api/automation/stats`, {
        params,
        withCredentials: true
      });

      return response.data;
    } catch (error) {
      console.error('Error getting automation stats:', error);
      throw error;
    }
  }

  /**
   * Get company automation settings
   */
  async getAutomationSettings(companyId) {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/automation/settings`, {
        params: { companyId },
        withCredentials: true
      });

      return response.data;
    } catch (error) {
      console.error('Error getting automation settings:', error);
      throw error;
    }
  }

  /**
   * Update company automation settings
   */
  async updateAutomationSettings(companyId, settings) {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/automation/settings/${companyId}`,
        settings,
        { withCredentials: true }
      );

      return response.data;
    } catch (error) {
      console.error('Error updating automation settings:', error);
      throw error;
    }
  }

  /**
   * Test creating a sample decision (development only)
   */
  async createTestDecision() {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/automation/test/decision`,
        {},
        { withCredentials: true }
      );

      return response.data;
    } catch (error) {
      console.error('Error creating test decision:', error);
      throw error;
    }
  }

  /**
   * Check automation service health
   */
  async checkHealth() {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/automation/health`, {
        withCredentials: true
      });

      return response.data;
    } catch (error) {
      console.error('Error checking automation health:', error);
      throw error;
    }
  }

  /**
   * Format decision type for display
   */
  formatDecisionType(decisionType) {
    if (!decisionType) return 'Unknown';
    
    return decisionType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Get status color for decision status
   */
  getStatusColor(status) {
    const colors = {
      'pending': '#f59e0b',      // yellow/amber
      'approved': '#10b981',     // green
      'rejected': '#ef4444',     // red
      'executed': '#06b6d4',     // cyan
      'failed': '#dc2626'        // dark red
    };
    
    return colors[status] || '#6b7280'; // gray fallback
  }

  /**
   * Get status icon for decision status
   */
  getStatusIcon(status) {
    const icons = {
      'pending': '⏳',
      'approved': '✅',
      'rejected': '❌',
      'executed': '🚀',
      'failed': '💥'
    };
    
    return icons[status] || '❓';
  }

  /**
   * Calculate automation performance metrics
   */
  calculatePerformanceMetrics(decisions) {
    if (!decisions || decisions.length === 0) {
      return {
        totalDecisions: 0,
        approvalRate: 0,
        executionRate: 0,
        averageResponseTime: 0
      };
    }

    const total = decisions.length;
    const approved = decisions.filter(d => d.status === 'approved').length;
    const executed = decisions.filter(d => d.status === 'executed').length;
    
    // Calculate average response time for approved/rejected decisions
    const respondedDecisions = decisions.filter(d => 
      d.status !== 'pending' && d.approved_at && d.approved_at !== '1970-01-01 00:00:00'
    );
    
    let averageResponseTime = 0;
    if (respondedDecisions.length > 0) {
      const totalResponseTime = respondedDecisions.reduce((sum, decision) => {
        const created = new Date(decision.created_at);
        const responded = new Date(decision.approved_at);
        return sum + (responded - created);
      }, 0);
      
      averageResponseTime = totalResponseTime / respondedDecisions.length / (1000 * 60 * 60); // Convert to hours
    }

    return {
      totalDecisions: total,
      approvalRate: total > 0 ? (approved / total * 100).toFixed(1) : 0,
      executionRate: approved > 0 ? (executed / approved * 100).toFixed(1) : 0,
      averageResponseTime: averageResponseTime.toFixed(1)
    };
  }
}

export default new AutomationService();
